import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { FaEdit, FaPlus, FaSearch, FaCamera, FaVideo } from "react-icons/fa";
import { fetchEvidenceById, evidenceFile } from "../http/api";
import Loading from "../loading/loading";
import SideNave from "../common/sidenav";
import TopNav from "../common/topnav";
import "../common/style.css";
import { toast } from "react-toastify";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB");
};

// File download helper
const handleDownload = async (fileName) => {
  if (!fileName) return;
  try {
    const response = await evidenceFile(fileName);
    const blob = new Blob([response.data], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error(err);
    toast.error("Failed to download file");
  }
};

export default function ShowEvidence() {
  const { batchId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    localStorage.getItem("sb|sidebar-toggle") === "true"
  );

  useEffect(() => {
    localStorage.setItem("sb|sidebar-toggle", isSidebarOpen);
  }, [isSidebarOpen]);

  const toggleSidebar = () => setIsSidebarOpen((s) => !s);

  // 🧠 Extract data and total count like batches pagination
  const extractDataAndTotal = (response) => {
    const data = response.data;
    if (data?.content) {
      return { items: data.content, total: data.totalElements || 0 };
    }
    if (Array.isArray(data)) {
      return { items: data, total: Number(response.headers?.["x-total-count"]) || data.length };
    }
    return { items: [], total: 0 };
  };

  // 🔁 Fetch evidence page (server-side pagination)
  const fetchEvidencePage = useCallback(
    async (pageNumber = 1, size = rowsPerPage, search = searchTerm) => {
      setLoading(true);
      try {
        const response = await fetchEvidenceById(batchId, pageNumber - 1, size, search);
        console.log("📡 Evidence API:", JSON.stringify(response.data, null, 2));
        const { items, total } = extractDataAndTotal(response);
        setData(items);
        setTotalRows(total);
      } catch (err) {
        console.error("❌ Error fetching evidence:", err);
        toast.error("Failed to load evidence data");
      } finally {
        setLoading(false);
      }
    },
    [batchId, rowsPerPage, searchTerm]
  );

  useEffect(() => {
    if (batchId) fetchEvidencePage(page, rowsPerPage, searchTerm);
  }, [batchId, page, rowsPerPage, searchTerm, fetchEvidencePage]);

  // 🔎 Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEvidencePage(1, rowsPerPage, searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchEvidencePage, rowsPerPage ]);

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const handlePageChange = (newPage) => setPage(newPage);
  const handlePerRowsChange = (newPerPage, newPage) => {
    setRowsPerPage(newPerPage);
    setPage(newPage);
  };

  const handleEditEvidence = (evidence) =>
    navigate(`/editEvidence/${batchId}/${evidence.id}`);
  const handleNewEvidence = () => navigate(`/batch/${batchId}/evidence`);

  const columns = [
    { name: "Evidence ID", selector: (row) => row.id, sortable: true, minWidth: "120px" },
    { name: "Date", selector: (row) => formatDate(row.date), sortable: true, minWidth: "130px" },
    {
      name: "Photos",
      minWidth: "120px",
      cell: (row) => {
        const photos = [row.photo1, row.photo2, row.photo3, row.photo4].filter(Boolean);
        if (photos.length === 0) return "No Photos";
        return (
          <div
            className="d-flex align-items-center"
            style={{ cursor: "pointer" }}
            title="Click to download photos"
            onClick={() => photos.forEach((f) => handleDownload(f))}
          >
            <FaCamera className="me-2 text-primary" />
            {photos.length}
          </div>
        );
      },
    },
    {
      name: "Video",
      minWidth: "100px",
      cell: (row) =>
        row.video1 ? (
          <div
            className="d-flex align-items-center"
            style={{ cursor: "pointer" }}
            title="Click to download video"
            onClick={() => handleDownload(row.video1)}
          >
            <FaVideo className="me-2 text-primary" /> 1
          </div>
        ) : (
          "No Video"
        ),
    },
    {
      name: "Actions",
      minWidth: "150px",
      cell: (row) => (
        <button className="btn btn-sm btn-primary" onClick={() => handleEditEvidence(row)}>
          <FaEdit /> Edit
        </button>
      ),
    },
  ];

  return (
    <div className="ShowEvidence">
      {loading && <Loading />}
      <div className={`sb-nav-fixed ${isSidebarOpen ? "sb-sidenav-toggled" : ""}`}>
        <TopNav toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} />
        <div id="layoutSidenav">
          <SideNave isSidebarOpen={isSidebarOpen} />
          <div id="layoutSidenav_content">
            <main>
              <div className="container-fluid px-4">
                <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
                  <h4>Evidence List for Batch {batchId}</h4>
                  <button className="btn btn-sm btn-success" onClick={handleNewEvidence}>
                    <FaPlus /> New Evidence
                  </button>
                </div>

                <div className="mb-3 d-flex justify-content-end">
                  <input
                    type="text"
                    placeholder="Search..."
                    className="form-control form-control-sm me-2"
                    style={{ width: "200px" }}
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                  <button className="btn btn-sm btn-secondary">
                    <FaSearch />
                  </button>
                </div>

                <div className="card mb-4">
                  <div className="card-header">
                    <i className="fas fa-table me-1"></i> Evidence Details
                  </div>
                  <div className="card-body shadow-sm border-0">
                    <DataTable
                      columns={columns}
                      data={data}
                      progressPending={loading}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={rowsPerPage}
                      paginationRowsPerPageOptions={[10, 20, 50, 100]}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePerRowsChange}
                      paginationDefaultPage={page}
                      highlightOnHover
                      responsive
                    />
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
